import React, { useEffect, useId, useRef, useState } from 'react';

import { FocusTrap } from 'focus-trap-react';
import { createPortal } from 'react-dom';

import { logger } from '@/utils/logger';
import { Z } from '@shared/constants/zIndex';
import { usePortalContainer, useScrollLock } from '@shared/hooks';

import { Button } from './Button';
import { Icons } from './icon-constants';

/** focus-trap-react's options type, derived from its props so we don't import `focus-trap` (a transitive-only dep) directly. */
type FocusTrapOptions = NonNullable<React.ComponentProps<typeof FocusTrap>['focusTrapOptions']>;

interface ModalProps {
  isOpen?: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'near-full' | 'fullscreen';
  /** Raise the height caps for non-fill sizes (xs–xl) so the modal can be
   *  taller — content up to ~86vh instead of the default ~70vh. No effect on
   *  near-full/fullscreen, which already manage their own height. */
  tall?: boolean;
  /** Give the panel a definite height and a flex column, and render children
   *  directly instead of inside a scrolling wrapper — for content that manages
   *  its own scroll regions (e.g. a two-pane picker whose list scrolls while
   *  its header and footer stay put).
   *
   *  Without this, sizes xs–xl wrap children in a height-auto
   *  `overflow-y-auto` box, so a `flex-1 min-h-0` child has no definite height
   *  to claim: it collapses to its content and the whole modal scrolls as one
   *  column. `fullscreen` and `near-full` already behave this way; `fill`
   *  brings it to the narrower sizes. */
  fill?: boolean;
  position?: 'center' | 'top';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  actions?: React.ReactNode;
  className?: string;
  /** Accessible name when no visible `title` is rendered (bare/custom-header modals). Ignored if `title` or `ariaLabelledBy` is set. */
  ariaLabel?: string;
  /** Id of a caller-rendered heading used as the accessible name. `title` slot wins over this; this wins over `ariaLabel`. */
  ariaLabelledBy?: string;
  /** Id of the element that describes the dialog (maps to aria-describedby). */
  ariaDescribedBy?: string;
  /** Replaces the backdrop's visual classes (background/blur). Structural classes (fixed inset-0, transition) always remain. */
  backdropClassName?: string;
  /** 'panel' (default) renders full chrome; 'bare' renders only children centered over the backdrop (lightbox). In 'bare', title/actions/showCloseButton are ignored. */
  variant?: 'panel' | 'bare';
  /** Element to focus when the modal opens (overrides the default first-focusable). E.g. a destructive dialog's Cancel button. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Raise this modal above other modal content (`Z.MODAL + 50`).
   *
   * For a modal that can itself be opened from *inside* another modal. Both
   * portal to the body, so at an equal z-index paint order falls to DOM order —
   * which happens to work today and is not something to depend on. Same offset
   * and reasoning as `ActionMenu`'s `aboveModal`; see
   * docs/lessons-learned/a-body-portaled-overlay-must-outrank-z-modal.md.
   */
  aboveModal?: boolean;
}

/** Size class lookup for the modal dialog width. */
const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  xs: 'max-w-sm',
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  // Centered but near-edge: ~94vw capped on large screens (see getMobileClasses for height).
  'near-full': 'max-w-[min(1600px,94vw)]',
  fullscreen: 'max-w-none w-full h-full',
};

/** Returns mobile-responsive classes based on modal size. */
function getMobileClasses(
  size: NonNullable<ModalProps['size']>,
  tall = false,
  fill = false
): string {
  if (size === 'fullscreen') return 'm-0 rounded-none h-full';
  // near-full: tall, rounded, centered (container padding supplies the backdrop margin).
  if (size === 'near-full') return 'm-0 rounded-xl h-[94vh] sm:h-[90vh]';
  const shell = 'sm:m-4 m-0 sm:rounded-xl rounded-none';
  if (fill) {
    // A *definite* height, not a cap: flex-1 has nothing to divide otherwise.
    // Full-bleed on mobile, matching the non-fill sizes at that width.
    return `${shell} h-full ${tall ? 'sm:h-[94vh]' : 'sm:h-[85vh]'}`;
  }
  const maxH = tall ? 'sm:max-h-[94vh]' : 'sm:max-h-[90vh]';
  return `${shell} ${maxH} max-h-full`;
}

/** Returns container layout classes for the modal positioning wrapper. */
function getContainerClasses(
  size: NonNullable<ModalProps['size']>,
  position: NonNullable<ModalProps['position']>
): string {
  if (size === 'fullscreen') return 'flex h-full p-0';
  if (position === 'top')
    return 'flex min-h-screen items-start justify-center pt-[15vh] p-0 sm:p-4';
  return 'flex min-h-screen items-center justify-center p-0 sm:p-4';
}

/** Panel classes for sizes whose body fills the height (fullscreen + near-full, or any
 *  size with `fill`): a flex column so the caller's content can claim the remaining
 *  space and scroll its own regions. */
function getFullscreenPanelClasses(size: NonNullable<ModalProps['size']>, fill = false): string {
  if (size === 'fullscreen') return 'flex h-full flex-col overflow-hidden';
  if (size === 'near-full') return 'flex flex-col overflow-hidden';
  return fill ? 'flex flex-col overflow-hidden' : '';
}

/** Panel surface classes. Non-fullscreen modals elevate to --card and recess their fields
 *  via --field; fullscreen modals keep the bare background (custom-surface modals paint
 *  their own panel, so overriding --field there would flatten their fields). */
function getPanelSurfaceClasses(size: NonNullable<ModalProps['size']>): string {
  return size === 'fullscreen' ? 'bg-background' : 'bg-card [--field:var(--field-sunken)]';
}

/** Enter/exit transition classes for the panel. */
function getVisibilityClass(isVisible: boolean): string {
  return isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0';
}

/** Ordered ids of currently-open modals. Only the last (top-most) one responds
 *  to Escape, so closing a nested modal never also closes its parent(s). */
const openModalStack: string[] = [];

/** Header section with optional title and close button. */
function ModalHeader({
  title,
  titleId,
  showCloseButton,
  onClose,
}: {
  title?: string | undefined;
  titleId: string;
  showCloseButton: boolean;
  onClose: () => void;
}) {
  if (!title && !showCloseButton) return null;

  return (
    <div className="border-border flex items-center justify-between border-b px-4 py-2">
      {title && (
        <h3
          data-testid="modal-title"
          id={titleId}
          className="text-foreground text-lg font-semibold"
        >
          {title}
        </h3>
      )}
      {showCloseButton && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="p-2"
          aria-label="Close modal"
        >
          <Icons.Close size="md" color="muted" />
        </Button>
      )}
    </div>
  );
}

/** Content area — renders children directly for sizes that manage their own height
 *  (fullscreen/near-full, or any size with `fill`), a scrollable wrapper otherwise. */
function ModalContent({
  size,
  tall = false,
  fill = false,
  children,
}: {
  size: NonNullable<ModalProps['size']>;
  tall?: boolean;
  fill?: boolean;
  children: React.ReactNode;
}) {
  if (fill || size === 'fullscreen' || size === 'near-full') {
    return <>{children}</>;
  }

  const maxH = tall ? 'sm:max-h-[86vh]' : 'sm:max-h-[70vh]';
  return (
    <div
      data-testid="modal-scroll-area"
      className={`max-h-[calc(100vh-8rem)] overflow-y-auto px-4 py-4 sm:px-6 ${maxH}`}
    >
      {children}
    </div>
  );
}

/** Optional footer actions bar. */
function ModalActions({ actions }: { actions?: React.ReactNode }) {
  if (!actions) return null;
  return (
    <div className="border-border flex justify-end space-x-3 border-t px-6 py-4">{actions}</div>
  );
}

/** Inner content of the modal: full panel chrome ('panel') or bare children ('bare', lightbox). */
function ModalInner({
  variant,
  size,
  tall = false,
  fill = false,
  className,
  visibilityClass,
  title,
  titleId,
  showCloseButton,
  onClose,
  onOverlayClick,
  actions,
  children,
}: {
  variant: 'panel' | 'bare';
  size: NonNullable<ModalProps['size']>;
  tall?: boolean | undefined;
  fill?: boolean | undefined;
  className: string;
  visibilityClass: string;
  title?: string | undefined;
  titleId: string;
  showCloseButton: boolean;
  onClose: () => void;
  /** Overlay-click handler. In 'bare' the wrapper is positioned above the
   *  backdrop, so wherever it has no content of its own (all of a fullscreen
   *  bottom sheet's scrim) a tap lands here, never on the backdrop. */
  onOverlayClick: (e: React.MouseEvent) => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (variant === 'bare') {
    return (
      <div
        data-testid="modal-bare"
        // Layout only; the keyboard path to the same dismissal is Escape.
        role="presentation"
        onClick={onOverlayClick}
        className={`relative w-full ${SIZE_CLASSES[size]} ${className} ${visibilityClass}`}
      >
        {children}
      </div>
    );
  }
  return (
    <div
      data-testid="modal-panel"
      className={`relative w-full ${SIZE_CLASSES[size]} border-border ${getPanelSurfaceClasses(size)} border shadow-[var(--modal-shadow)] transition-all duration-200 ease-out ${getFullscreenPanelClasses(size, fill)} ${getMobileClasses(size, tall, fill)} ${className} ${visibilityClass}`}
    >
      <ModalHeader
        title={title}
        titleId={titleId}
        showCloseButton={showCloseButton}
        onClose={onClose}
      />
      <ModalContent size={size} tall={tall} fill={fill}>
        {children}
      </ModalContent>
      <ModalActions actions={actions} />
    </div>
  );
}

/** Semi-transparent backdrop overlay behind the modal. */
function ModalBackdrop({
  isVisible,
  onClick,
  backdropClassName,
}: {
  isVisible: boolean;
  onClick: (e: React.MouseEvent) => void;
  backdropClassName?: string | undefined;
}) {
  const visual =
    backdropClassName ?? 'bg-[var(--modal-backdrop)] backdrop-blur-[var(--modal-backdrop-blur)]';
  return (
    <div
      className={`fixed inset-0 ${visual} transition-opacity duration-200 ease-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClick}
      aria-hidden="true"
    />
  );
}

/** Resolves dialog accessible-name attributes by precedence: title slot → ariaLabelledBy → ariaLabel. */
function resolveAriaName(
  hasTitle: boolean,
  titleId: string,
  ariaLabelledBy: string | undefined,
  ariaLabel: string | undefined
): { 'aria-labelledby'?: string; 'aria-label'?: string } {
  if (hasTitle) return { 'aria-labelledby': titleId };
  if (ariaLabelledBy) return { 'aria-labelledby': ariaLabelledBy };
  if (ariaLabel) return { 'aria-label': ariaLabel };
  return {};
}

/** The outer scroll behaviour: fullscreen clips, everything else scrolls. */
function getOverflowClass(size: NonNullable<ModalProps['size']>): string {
  return size === 'fullscreen' ? 'overflow-hidden' : 'overflow-y-auto';
}

/**
 * Stacking level for the dialog.
 *
 * A modal opened from inside another modal must outrank its host: both portal
 * to the body, so at an equal z-index paint order falls to DOM order, which
 * works by accident rather than by design. Same offset as `ActionMenu`'s
 * `aboveModal`.
 */
function modalZIndex(aboveModal: boolean | undefined): number {
  return aboveModal ? Z.MODAL + 50 : Z.MODAL;
}

/** Whether the visible `title` slot renders. In 'bare' the title is ignored, so it never participates in the accessible name. */
function hasVisibleTitle(title: string | undefined, variant: 'panel' | 'bare'): boolean {
  return variant === 'panel' && Boolean(title);
}

/** focus-trap-react options for a Modal that don't depend on its refs: permissive so Modal keeps
 *  owning Escape/overlay-click and body-portaled popovers stay mouse-usable; return focus on close.
 *  Initial focus is applied synchronously (`delayInitialFocus: false`) since the Modal already
 *  controls its own mount timing. */
const STATIC_FOCUS_TRAP_OPTIONS = {
  delayInitialFocus: false,
  escapeDeactivates: false,
  clickOutsideDeactivates: false,
  allowOutsideClick: true,
  returnFocusOnDeactivate: true,
  tabbableOptions: { displayCheck: 'none' as const },
} satisfies FocusTrapOptions;

/** Initial focus → `initialFocusRef` (else the dialog). Without one it returns `undefined`,
 *  focus-trap's "first focusable" default. focus-trap calls it at activation, never in render. */
function initialFocusTarget(
  dialogRef: React.RefObject<HTMLDivElement | null>,
  initialFocusRef: React.RefObject<HTMLElement | null> | undefined
): HTMLElement | false | undefined {
  if (!initialFocusRef) return undefined;
  return initialFocusRef.current ?? dialogRef.current ?? false;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen = true,
  onClose,
  title,
  children,
  size = 'md',
  // No default here: ModalInner and ModalContent already default it, and a
  // default value counts as a branch against this function's complexity budget.
  fill,
  tall,
  position = 'center',
  showCloseButton = true,
  closeOnOverlayClick = true,
  actions,
  className = '',
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  backdropClassName,
  variant = 'panel',
  initialFocusRef,
  aboveModal,
}) => {
  // Track whether the modal should be rendered (stays true during exit animation)
  const [shouldRender, setShouldRender] = useState(isOpen);
  // Track the visible state for CSS transitions
  const [isVisible, setIsVisible] = useState(false);
  const titleId = useId();
  const instanceId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Per-instance portal container under #modal-root (fallback: document.body). Created in an
  // effect (StrictMode-safe — no detached-node leak) and removed on unmount.
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  // A fullscreen element OUTRANKS #modal-root: the browser paints only the
  // fullscreen subtree, so a modal parented outside it is in the document,
  // focus-trapped, and never drawn — the dialog equivalent of a hang.
  const fullscreenRoot = usePortalContainer();

  useEffect(() => {
    const el = document.createElement('div');
    el.setAttribute('data-modal-portal', '');
    const root =
      fullscreenRoot === document.body
        ? (document.getElementById('modal-root') ?? document.body)
        : fullscreenRoot;
    root.appendChild(el);
    setPortalContainer(el);
    return () => {
      el.remove(); // no-op if already detached; safer than root.removeChild
      setPortalContainer(null);
    };
  }, [fullscreenRoot]);

  // Dev-only: every modal must have an accessible name. logger.warn is a no-op outside development.
  useEffect(() => {
    if (!title && !ariaLabelledBy && !ariaLabel) {
      logger.warn(
        'Modal: provide a `title`, `ariaLabelledBy`, or `ariaLabel` so the dialog has an accessible name.'
      );
    }
  }, [title, ariaLabelledBy, ariaLabel]);

  // Handle mount/unmount with animation
  useEffect(() => {
    if (isOpen) {
      // Mount the modal first
      setShouldRender(true);
      // Then trigger the enter animation on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      return undefined;
    } else {
      // Start exit animation
      setIsVisible(false);
      // Unmount after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200); // Match the CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open (with reference counting for nested modals)
  useScrollLock(isOpen);

  // Track open modals in a stack so only the top-most one reacts to Escape.
  useEffect(() => {
    if (!isOpen) return;
    openModalStack.push(instanceId);
    return () => {
      const i = openModalStack.lastIndexOf(instanceId);
      if (i !== -1) openModalStack.splice(i, 1);
    };
  }, [isOpen, instanceId]);

  // Handle escape key — only the top-most modal closes, leaving parents open.
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (openModalStack[openModalStack.length - 1] !== instanceId) return;
      onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, instanceId]);

  if (!shouldRender || !portalContainer) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Closures built here, not a helper that takes the refs: handing a ref to a function
  // during render is a ref read the React Compiler can't prove safe, so it skipped Modal.
  const focusTrapOptions: FocusTrapOptions = {
    ...STATIC_FOCUS_TRAP_OPTIONS,
    initialFocus: () => initialFocusTarget(dialogRef, initialFocusRef),
    fallbackFocus: () => dialogRef.current as HTMLElement,
  };
  const overflowClass = getOverflowClass(size);
  const visibilityClass = getVisibilityClass(isVisible);
  const ariaName = resolveAriaName(
    hasVisibleTitle(title, variant),
    titleId,
    ariaLabelledBy,
    ariaLabel
  );

  return createPortal(
    <FocusTrap focusTrapOptions={focusTrapOptions}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        data-testid="modal"
        className={`fixed inset-0 ${overflowClass}`}
        style={{ zIndex: modalZIndex(aboveModal) }}
        {...ariaName}
        aria-describedby={ariaDescribedBy}
        role="dialog"
        aria-modal="true"
      >
        <ModalBackdrop
          isVisible={isVisible}
          onClick={handleOverlayClick}
          backdropClassName={backdropClassName}
        />

        {/* Modal container */}
        <div className={getContainerClasses(size, position)}>
          <ModalInner
            variant={variant}
            size={size}
            tall={tall}
            fill={fill}
            className={className}
            visibilityClass={visibilityClass}
            title={title}
            titleId={titleId}
            showCloseButton={showCloseButton}
            onClose={onClose}
            onOverlayClick={handleOverlayClick}
            actions={actions}
          >
            {children}
          </ModalInner>
        </div>
      </div>
    </FocusTrap>,
    portalContainer
  );
};
